/**
 * Alfie: the client side of a live voice call on OpenAI's Realtime API.
 *
 * Transport-free, so the web app (WebRTC in the page) and the mobile app (WebRTC in a WebView)
 * share it: feed it the data channel's server events, and it reports the call's phase and
 * transcript, runs the `ask_bot` and `end_call` tools, and sends client events back.
 */

export type RealtimePhase = "listening" | "thinking" | "speaking";

/** Longest the voice waits on the full bot before telling the caller it is still working. */
export const REALTIME_ASK_TIMEOUT_MS = 180_000;
/** A goodbye whose audio never reports its end still hangs up. */
export const REALTIME_HANG_UP_FALLBACK_MS = 8_000;

export type RealtimeControllerDeps = {
  /** Sends one client event over the data channel. */
  send: (event: Record<string, unknown>) => void;
  /** Hands a request to the bot's full agent; resolves with its reply. */
  askBot: (request: string) => Promise<string>;
  onPhase: (phase: RealtimePhase) => void;
  /** The caller's words so far in the current turn; empty once it is filed. */
  onHeard: (text: string) => void;
  onExchange: (role: "user" | "bot", text: string) => void;
  /** The voice said goodbye and its audio finished. */
  onHangUp: () => void;
  onError: (message: string) => void;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
};

export type RealtimeController = {
  handleEvent: (raw: unknown) => void;
  /** The call connected: the voice speaks first, optionally with the bot's own opening. */
  greet: (opening?: string) => void;
  close: () => void;
};

type ServerEvent = { type?: unknown; [key: string]: unknown };

type FunctionCallItem = { type?: unknown; name?: unknown; call_id?: unknown; arguments?: unknown };

function parseEvent(raw: unknown): ServerEvent | null {
  if (typeof raw === "string") {
    try {
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? (parsed as ServerEvent) : null;
    } catch {
      return null;
    }
  }
  return raw && typeof raw === "object" ? (raw as ServerEvent) : null;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function createRealtimeController(deps: RealtimeControllerDeps): RealtimeController {
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer =
    deps.clearTimer ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  let closed = false;
  /** A response is being generated: a new `response.create` waits for it to finish. */
  let responding = false;
  let responseQueued = false;
  /** Audio is playing on the caller's side. */
  let speaking = false;
  let pendingAsks = 0;
  let hangingUp = false;
  let hangUpTimer: unknown = null;
  const handledCalls = new Set<string>();
  const heard = new Map<string, string>();

  const send = (event: Record<string, unknown>) => {
    if (!closed) deps.send(event);
  };

  const requestResponse = (response?: Record<string, unknown>) => {
    if (closed) return;
    if (responding) {
      responseQueued = true;
      return;
    }
    responding = true;
    send(response ? { type: "response.create", response } : { type: "response.create" });
  };

  const idlePhase = (): RealtimePhase => (pendingAsks > 0 ? "thinking" : "listening");

  const finishHangUp = () => {
    if (closed) return;
    if (hangUpTimer !== null) clearTimer(hangUpTimer);
    hangUpTimer = null;
    deps.onHangUp();
  };

  const answerTool = (callId: string, output: string) => {
    send({
      type: "conversation.item.create",
      item: { type: "function_call_output", call_id: callId, output },
    });
    requestResponse();
  };

  const runTool = (item: FunctionCallItem) => {
    const callId = text(item.call_id);
    if (!callId || handledCalls.has(callId)) return;
    handledCalls.add(callId);
    const name = text(item.name);
    if (name === "end_call") {
      hangingUp = true;
      if (!speaking) finishHangUp();
      else hangUpTimer = setTimer(finishHangUp, REALTIME_HANG_UP_FALLBACK_MS);
      return;
    }
    if (name !== "ask_bot") {
      answerTool(callId, JSON.stringify({ error: `Unknown tool ${name}` }));
      return;
    }
    let request = "";
    try {
      const args: unknown = JSON.parse(typeof item.arguments === "string" ? item.arguments : "{}");
      request = text((args as { request?: unknown }).request);
    } catch {
      request = "";
    }
    if (!request) {
      answerTool(callId, JSON.stringify({ error: "The request was empty." }));
      return;
    }
    pendingAsks += 1;
    if (!speaking) deps.onPhase("thinking");
    let timer: unknown = null;
    const timedOut = new Promise<string>((resolve) => {
      timer = setTimer(() => resolve("__timeout__"), REALTIME_ASK_TIMEOUT_MS);
    });
    void Promise.race([deps.askBot(request), timedOut])
      .then(
        (reply) =>
          reply === "__timeout__"
            ? {
                status: "still_working",
                note: "The bot is still working on it and will post the answer in the chat.",
              }
            : { status: "done", reply: reply.trim() || "The bot finished without a reply." },
        (error: unknown) => ({
          status: "failed",
          error: error instanceof Error ? error.message : "The bot could not be reached.",
        }),
      )
      .then((result) => {
        if (timer !== null) clearTimer(timer);
        pendingAsks = Math.max(0, pendingAsks - 1);
        if (closed) return;
        answerTool(callId, JSON.stringify(result));
        if (!speaking) deps.onPhase(idlePhase());
      });
  };

  return {
    handleEvent(raw) {
      if (closed) return;
      const event = parseEvent(raw);
      if (!event) return;
      switch (event.type) {
        case "input_audio_buffer.speech_started":
          deps.onPhase("listening");
          break;
        case "input_audio_buffer.speech_stopped":
          deps.onPhase("thinking");
          break;
        case "conversation.item.input_audio_transcription.delta": {
          const itemId = text(event.item_id);
          const next =
            (heard.get(itemId) ?? "") + (typeof event.delta === "string" ? event.delta : "");
          heard.set(itemId, next);
          deps.onHeard(next.trim());
          break;
        }
        case "conversation.item.input_audio_transcription.completed": {
          heard.delete(text(event.item_id));
          deps.onHeard("");
          const said = text(event.transcript);
          if (said) deps.onExchange("user", said);
          break;
        }
        case "response.created":
          responding = true;
          break;
        case "output_audio_buffer.started":
          speaking = true;
          deps.onPhase("speaking");
          break;
        case "output_audio_buffer.stopped":
        case "output_audio_buffer.cleared":
          speaking = false;
          if (hangingUp) finishHangUp();
          else deps.onPhase(idlePhase());
          break;
        case "response.output_audio_transcript.done": {
          const said = text(event.transcript);
          if (said) deps.onExchange("bot", said);
          break;
        }
        case "response.done": {
          responding = false;
          const response = (event.response ?? {}) as {
            status?: unknown;
            status_details?: { error?: { message?: unknown } };
            output?: FunctionCallItem[];
          };
          if (response.status === "failed") {
            deps.onError(text(response.status_details?.error?.message) || "The voice stopped.");
          }
          for (const item of Array.isArray(response.output) ? response.output : []) {
            if (item?.type === "function_call") runTool(item);
          }
          if (responseQueued) {
            responseQueued = false;
            requestResponse();
          }
          break;
        }
        case "error": {
          const error = (event.error ?? {}) as { code?: unknown; message?: unknown };
          // A queued response raced one the server started on its own: harmless.
          if (error.code === "conversation_already_has_active_response") {
            responseQueued = true;
            break;
          }
          deps.onError(text(error.message) || "The voice service reported an error.");
          break;
        }
        default:
          break;
      }
    },
    greet(opening) {
      // Response-level instructions would replace the session's persona, so the bot's own
      // opening goes in as a system note and the session instructions cover the greeting.
      const line = opening?.trim();
      if (line) {
        send({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [
              {
                type: "input_text",
                text: `You called the caller. Open the call by telling them this, briefly and in your own words: ${line}`,
              },
            ],
          },
        });
      }
      requestResponse();
    },
    close() {
      closed = true;
      if (hangUpTimer !== null) clearTimer(hangUpTimer);
      hangUpTimer = null;
    },
  };
}
